import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AdminShell } from "@/components/Layout";
import BrandedHero from "@/components/BrandedHero";
import WeatherWidget from "@/components/WeatherWidget";
import { api } from "@/lib/api";
import { CaretLeft, CaretRight, Calendar, Truck, Wrench, CloudRain, Sun } from "@phosphor-icons/react";

/**
 * AdminScheduleMonth — CP-style month grid for pinned WOs.
 *
 * • Fetches 5-6 weeks of `/api/jobs/sweeper-pins/week` in parallel, unions all
 *   cells, and buckets by date so each calendar day gets: total count,
 *   per-truck breakdown, top-3 WO chips.
 * • Overlays per-day weather (from cached `/api/weather/forecast`) so the
 *   office sees wet days at a glance — helpful when scheduling roof/exterior
 *   work.
 * • Click a day → jumps to `/admin/schedule?start=<monday-of-that-week>` week
 *   view, ready to slot more.
 */

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (base, n) => { const d = new Date(base); d.setDate(d.getDate() + n); return d; };
const parseIsoLocal = (s) => { const [y, m, d] = String(s || "").split("-").map(Number); return new Date(y, (m || 1) - 1, d || 1); };

function mondayOf(date) {
  const d = new Date(date);
  const dow = d.getDay();
  const diff = dow === 0 ? -6 : 1 - dow;
  return addDays(d, diff);
}
function firstOfMonth(y, m) { return new Date(y, m, 1); }
function daysInMonth(y, m) { return new Date(y, m + 1, 0).getDate(); }

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function AdminScheduleMonth() {
  const nav = useNavigate();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth()); // 0-based
  const [byDate, setByDate] = useState({});
  const [truckLabels, setTruckLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [weather, setWeather] = useState({}); // date → day object

  // Build the 6-week grid starting Monday of week containing day-1
  const gridDays = useMemo(() => {
    const first = firstOfMonth(year, month);
    const startMonday = mondayOf(first);
    return Array.from({ length: 42 }, (_, i) => addDays(startMonday, i));
  }, [year, month]);

  const gridStart = gridDays[0];
  const gridEnd = gridDays[41];

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // 6 weekly fetches in parallel — API is fast + cached, this is cheap
    const weekStarts = Array.from({ length: 6 }, (_, i) => iso(addDays(gridStart, i * 7)));
    Promise.all(
      weekStarts.map(s => api.get(`/jobs/sweeper-pins/week?start=${s}`).then(r => r.data).catch(() => null))
    ).then(results => {
      if (cancelled) return;
      const bd = {};
      const tl = {};
      for (const wk of results) {
        if (!wk) continue;
        Object.assign(tl, wk.truck_labels || {});
        for (const [key, jobs] of Object.entries(wk.cells || {})) {
          const [tk, d] = key.split("|");
          if (!bd[d]) bd[d] = { total: 0, jobs: [], byTruck: {} };
          bd[d].total += jobs.length;
          bd[d].byTruck[tk] = (bd[d].byTruck[tk] || 0) + jobs.length;
          for (const j of jobs) bd[d].jobs.push({ ...j, truck_key: tk });
        }
      }
      setByDate(bd);
      setTruckLabels(tl);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [gridStart.getTime()]);

  // Load 16-day weather (Open-Meteo max), covers most of the month grid
  useEffect(() => {
    let cancelled = false;
    api.get("/weather/forecast?days=16").then(r => {
      if (cancelled) return;
      const map = {};
      for (const d of r.data?.days || []) map[d.date] = d;
      setWeather(map);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const monthTotal = useMemo(() =>
    gridDays
      .filter(d => d.getMonth() === month)
      .reduce((sum, d) => sum + (byDate[iso(d)]?.total || 0), 0)
  , [gridDays, byDate, month]);

  const goto = (delta) => {
    let m = month + delta, y = year;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setMonth(m); setYear(y);
  };
  const gotoToday = () => {
    const t = new Date(); setMonth(t.getMonth()); setYear(t.getFullYear());
  };

  const onDayClick = (d) => {
    nav(`/admin/schedule?start=${iso(mondayOf(d))}`);
  };

  return (
    <AdminShell>
      <BrandedHero
        breadcrumbs={[{ to: "/admin", label: "Home" }, { to: "/admin/schedule", label: "Schedule" }, { label: "Month" }]}
        eyebrow={`${monthTotal} WO${monthTotal === 1 ? "" : "s"} pinned this month`}
        title={`${MONTH_NAMES[month]} ${year}`}
        subtitle="Full-month schedule with per-day weather overlay. Click any day to open that week."
        size="lg"
        actions={
          <>
            <div className="flex rounded-xl bg-white/15 backdrop-blur p-1 gap-1">
              <Link to="/admin/schedule" data-testid="month-view-week-toggle"
                className="h-8 px-3 rounded-lg text-white text-[10px] font-black uppercase tracking-widest hover:bg-white/15 inline-flex items-center">
                Week
              </Link>
              <span data-testid="month-view-current-toggle"
                className="h-8 px-3 rounded-lg bg-white text-slate-900 text-[10px] font-black uppercase tracking-widest inline-flex items-center">
                Month
              </span>
            </div>
            <button onClick={gotoToday} data-testid="month-view-today-btn"
              className="h-9 px-3 rounded-xl bg-white text-slate-900 text-[10px] font-black uppercase tracking-widest hover:bg-slate-100">
              Today
            </button>
          </>
        }
      />

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 -mt-16 relative z-10 pb-8">
        {/* Weather widget row (7-day strip, current-day card) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4" data-testid="month-view-weather-row">
          <div className="lg:col-span-1">
            <WeatherWidget variant="card" tone="gradient" days={7} testid="month-weather-card"/>
          </div>
          <div className="lg:col-span-2 flex items-center gap-2">
            <button onClick={() => goto(-1)} data-testid="month-view-prev"
              className="h-10 w-10 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 flex items-center justify-center shrink-0 shadow-sm">
              <CaretLeft size={18} weight="bold"/>
            </button>
            <div className="flex-1 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center gap-2 shadow-sm">
              <Calendar size={16} weight="fill" className="text-cyan-600"/>
              <span className="text-sm font-black uppercase tracking-widest text-slate-800" data-testid="month-view-label">
                {MONTH_NAMES[month]} {year}
              </span>
            </div>
            <button onClick={() => goto(1)} data-testid="month-view-next"
              className="h-10 w-10 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 flex items-center justify-center shrink-0 shadow-sm">
              <CaretRight size={18} weight="bold"/>
            </button>
          </div>
        </div>

        {/* Month grid */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="month-view-grid">
          {/* Day-of-week headers */}
          <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50">
            {DOW.map(d => (
              <div key={d} className="px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-500 text-center">
                {d}
              </div>
            ))}
          </div>

          {/* Days */}
          <div className="grid grid-cols-7">
            {gridDays.map((d, i) => {
              const dIso = iso(d);
              const inMonth = d.getMonth() === month;
              const isToday = iso(d) === iso(today);
              const cell = byDate[dIso] || { total: 0, jobs: [], byTruck: {} };
              const wx = weather[dIso];
              const isWet = wx?.is_wet;
              const wxHi = wx?.hi;

              return (
                <button
                  key={dIso}
                  data-testid={`month-day-${dIso}`}
                  onClick={() => onDayClick(d)}
                  className={`text-left min-h-[110px] p-2 border-r border-b border-slate-100 last:border-r-0 relative transition-colors ${
                    inMonth ? "bg-white hover:bg-slate-50" : "bg-slate-50/50 hover:bg-slate-100"
                  } ${isToday ? "ring-2 ring-cyan-400 ring-inset z-10" : ""}`}
                >
                  {/* Header row: date + weather */}
                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-black tabular-nums ${
                      isToday ? "text-cyan-700"
                        : inMonth ? "text-slate-900" : "text-slate-400"
                    }`}>
                      {d.getDate()}
                      {isToday && <span className="ml-1 text-[9px] font-black uppercase tracking-widest text-cyan-600">Today</span>}
                    </span>
                    {wx && (
                      <span className="flex items-center gap-0.5" data-testid={`month-day-${dIso}-weather`}>
                        {isWet ? (
                          <CloudRain size={12} weight="fill" className="text-cyan-500"/>
                        ) : (
                          <Sun size={12} weight="fill" className="text-amber-400"/>
                        )}
                        <span className="text-[9px] font-black tabular-nums text-slate-500">{wxHi}°</span>
                      </span>
                    )}
                  </div>

                  {/* WO summary */}
                  {cell.total > 0 && (
                    <div className="mt-1.5 space-y-1">
                      <div className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-slate-500">
                        <Wrench size={10} weight="fill"/>
                        <span data-testid={`month-day-${dIso}-count`}>{cell.total} WO{cell.total === 1 ? "" : "s"}</span>
                      </div>
                      {/* Top 3 job chips */}
                      {cell.jobs.slice(0, 3).map((j, idx) => (
                        <div key={j.id + idx}
                          className={`text-[10px] leading-tight truncate rounded px-1.5 py-0.5 border ${
                            j.is_turn ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                              : j.priority === "critical" || j.priority === "urgent" || j.priority === "high"
                                ? "bg-red-50 border-red-200 text-red-800"
                                : "bg-slate-50 border-slate-200 text-slate-700"
                          }`}
                          title={`${j.addr || ""}${j.unit ? " · " + j.unit : ""} · ${j.title || ""}`}
                        >
                          {j.addr || j.title || "WO"}
                        </div>
                      ))}
                      {cell.jobs.length > 3 && (
                        <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                          +{cell.jobs.length - 3} more
                        </div>
                      )}
                      {/* Truck bubbles */}
                      {Object.keys(cell.byTruck).length > 0 && (
                        <div className="flex items-center gap-1 pt-0.5 flex-wrap">
                          {Object.entries(cell.byTruck).slice(0, 4).map(([tk, ct]) => (
                            <span key={tk}
                              className="text-[9px] font-black bg-cyan-50 text-cyan-700 border border-cyan-200 rounded-full px-1.5 py-0"
                              title={truckLabels[tk] || tk}>
                              {(truckLabels[tk] || tk).slice(0, 4)}·{ct}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {cell.total === 0 && inMonth && !loading && (
                    <div className="mt-3 text-[9px] font-black uppercase tracking-widest text-slate-300">
                      Empty
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {loading && (
          <div className="mt-3 text-center text-[11px] font-black uppercase tracking-widest text-slate-400">
            Loading pinned WOs…
          </div>
        )}
      </div>
    </AdminShell>
  );
}
